(ns app.front.view.group.admin.inspections.checklists.checklists-v2
  "Group admin config section for inspection checklists, including their items and item groups,
  defined in `app.front.view.group.admin.inspections.checklist-items`"
  (:require [ajax.core :refer [POST]]
            [app.domain.inspection.model :as inspection-model]
            [app.domain.util :as du]
            [app.front.entities :as entities]
            [app.front.entity-edit :as ee]
            [app.front.file.upload :as upload]
            [app.front.ui :as ui]
            [app.front.ui.column-select :as columns]
            [app.front.ui.dnd :as dnd]
            [app.front.ui.i18n :as ui-i18n]
            [app.front.ui.modal :as ui-modal]
            [app.front.ui.select :as ui-select]
            [app.front.ui.separator :as ui-sep]
            [app.front.ui.tab-panel :as tab-panel]
            [app.front.view.group.admin.inspections.checklist-result-sets :as inspection-checklist-result-sets]
            [app.front.view.group.admin.inspections.checklists.checklist-items :as inspection-checklist-items]
            [app.front.view.group.admin.inspections.checklists.subs :as checklist-subs]
            [app.front.view.group.admin.subs :as gas]
            [app.front.view.inspections.util :as inspection-util]
            [app.i18n.model :as i18n-model]
            [clojure.string :as str]
            [core.front.carbon :as cc]
            [core.front.date-util :as date-util]
            [core.front.event :refer [<sub >evt]]
            [core.front.icons :as i]
            [core.util.util :as util]
            [reagent.core :as r]))


(defn checklist-name [checklist-template language]
  (i18n-model/get-text (:inspection.checklist/names checklist-template)
                       language))

(defn checklist-row [{:keys [tr language format-date-time selected-checklist-id augment-target-props augment-draggable-props reordering?]}
                     {id :db/id :as checklist-template}]
  (let [container-opts (augment-target-props {} checklist-template)
        draggable-opts (augment-draggable-props {} checklist-template)]
    {:id        id
     :selected? (= id selected-checklist-id)
     :content   [:div.grid.grid-cols-12.gap-2
                 (r/merge-props container-opts (when reordering? draggable-opts))
                 [:div.col-span-3
                  (checklist-name checklist-template language)]
                 [:div.col-span-5
                  {:class (when (not= id selected-checklist-id) "text-gray-600")}
                  (when-let [change (:latest-change checklist-template)]
                    (str (format-date-time (:ts change)) " (" (:user change) ")"))]
                 [:div.col-span-2.uppercase.bold.flex.justify-end
                  (when (:inspection.template.kind/_default-checklist-templates checklist-template)
                    (tr [:inspection.checklist/default]))]
                 [:div.col-span-2.flex.justify-end
                  (when reordering?
                    (cc/icon {:icon        i/draggable
                              :description (tr [:reorder])
                              :fill        "currentcolor"}))]]}))

(defn- stop-tracking-file
  [component-id file-hash]
  (>evt [::upload/remove-pending-file component-id :placeholder])
  (>evt [::upload/remove-pending-file component-id file-hash]))

(defn- execute-import
  "Executes the actual import with the given conflict resolution strategy"
  [kind-id file component-id file-hash conflict-resolution]
  (let [url (str "/api/command/inspection/import-inspection-checklists"
                 "?target-inspection-template-kind-id=" kind-id
                 (when conflict-resolution
                   (str "&conflict-resolution=" (name conflict-resolution))))]
    (POST
      url
      {:headers       {"Content-Type" "application/edn"}
       :body          file
       :error-handler (fn [{:keys [status response]}]
                        (stop-tracking-file component-id file-hash)
                        (>evt [::upload/upload-failure (util/mmap {:md5 file-hash} component-id) status response]))
       :handler       (fn [response]
                        (>evt [::entities/apply-changes 200 response])
                        (stop-tracking-file component-id file-hash)
                        (>evt [:notifications/add :toast :success :success :inspection.checklist/import-success]))})))

(defn process-import-file
  "Called when the user selects the imported EDN file in the UI.
   First detects conflicts, then shows dialog if needed."
  [kind-id {:keys [file component-id file-hash]}]
  (>evt [::upload/add-pending-file component-id file-hash])
  ;; First, detect conflicts
  (POST
    (str "/api/command/inspection/detect-checklist-conflicts?target-inspection-template-kind-id=" kind-id)
    {:headers       {"Content-Type" "application/edn"}
     :body          file
     :error-handler (fn [{:keys [status response]}]
                      (stop-tracking-file component-id file-hash)
                      (>evt [::upload/upload-failure (util/mmap {:md5 file-hash} component-id) status response]))
     :handler       (fn [response]
                      (let [conflicts (get response :conflicts)]
                        (if (seq conflicts)
                          ;; Store file info and show conflict resolution dialog
                          (>evt [::checklist-subs/show-import-conflict-dialog
                                 {:kind-id      kind-id
                                  :file         file
                                  :component-id component-id
                                  :file-hash    file-hash
                                  :conflicts    conflicts}])
                          ;; No conflicts, proceed with import
                          (execute-import kind-id file component-id file-hash nil))))}))

(defn import-button [{:keys [tr kind-id]}]
  [upload/file-picker-button
   {:id           (str "checklist-import-" kind-id)
    :tr           tr
    :accept-types [".edn"]
    :button-opts  {:size             :small
                   :kind             :ghost
                   :icon             i/upload
                   :icon-description (tr [:inspection.checklist/import])}
    :on-select-fn (partial process-import-file kind-id)}
   (tr [:inspection.checklist/import])])

(defn checklist-selector
  "List of all inspection checklist templates under the currently existing
  inspection template kinds (= inspection types) and a button to add new ones"
  [{:keys [tr language]}]
  (let [kind-id               (<sub [::checklist-subs/selected-template-kind-id])
        template-id           (<sub [::checklist-subs/selected-checklist-template-id])
        checklists            (<sub [::checklist-subs/inspection-checklist-templates])
        selected-checklist-id (<sub [::checklist-subs/selected-checklist-template-id])
        format-date-time      (<sub [::date-util/date-time-fn])
        reordering?           (<sub [::checklist-subs/reordering-checklist-templates?])
        rename-popped-up?     (<sub [::ee/has-unsaved-for-any?
                                     #{:inspection.checklist/names :inspection.checklist-item.group/names}])]
    [:div.grid.grid-cols-6.gap-4 {:data-cy "checklist-selector"}
     [:div.col-span-2
      [columns/column-select
       {:tr        tr
        :title     :inspection/available-template-kinds
        :light?    true
        :disabled? rename-popped-up?
        :options   (<sub [::checklist-subs/inspection-template-kind-items])
        :on-change #(>evt [::checklist-subs/select-template-kind %])}]]
     [:div.col-span-4
      [dnd/wrap-dnd
       {:id               (str "checklists-dnd-" template-id)
        :items            checklists
        :props-for-source {:class "translate-x-2"}
        :on-reorder       (fn [from to]
                            (>evt [::checklist-subs/reorder-checklist-templates from to]))}
       (fn [{:keys [augment-draggable-props augment-target-props sorted]}]
         [columns/column-select
          {:tr        tr
           :title     :inspection/checklists-for-type
           :light?    true
           :disabled? rename-popped-up?
           :options   (map (partial checklist-row (util/mmap tr
                                                             language
                                                             selected-checklist-id
                                                             format-date-time
                                                             augment-draggable-props
                                                             augment-target-props
                                                             reordering?))
                           sorted)
           :on-change #(>evt [::checklist-subs/select-checklist-template %])
           :actions   [(when kind-id
                         {:component [import-button {:tr      tr
                                                     :kind-id kind-id}]})
                       (when kind-id
                         {:tr-key   :inspection.checklist/export
                          :icon     i/download
                          :on-click #(>evt [::checklist-subs/open-export-modal])})
                       (when (seq checklists)
                         {:tr-key   :inspection.checklist/copy-checklist
                          :icon     i/copy
                          :on-click #(>evt [::checklist-subs/open-copy-modal])})
                       (when (seq checklists)
                         {:tr-key   (if reordering? :inspection.checklist/reorder-done :inspection.checklist/reorder)
                          :icon     i/arrows-up-down
                          :on-click #(>evt [::checklist-subs/toggle-reordering-checklist-templates])})
                       (when kind-id
                         {:tr-key   :inspection.checklist/add-new
                          :icon     i/plus
                          :on-click #(>evt [::checklist-subs/create-checklist-template kind-id language])})]}])]]]))

(defn checklist-editor-buttons
  [{:keys [tr]}]
  (let [reordering?       (<sub [::checklist-subs/reordering?])
        rename-popped-up? (<sub [::ee/has-unsaved-for-any?
                                 #{:inspection.checklist/names :inspection.checklist-item.group/names}])]
    [:div.flex.flex-row.gap-3
     [ui/modal-confirmation-button
      {:tr                  tr
       :id                  "delete-checklist-template"
       :disabled?           (or reordering? rename-popped-up?)
       :on-yes-click        #(>evt [::checklist-subs/delete-checklist-template])
       :trigger-button-text (tr [:inspection.checklist/delete])
       :trigger-button-opts {:kind :ghost
                             :size :small
                             :icon i/trash}
       :modal-title         (tr [:are-you-sure])}]
     (cc/button {:kind     :ghost
                 :size     :small
                 :disabled rename-popped-up?
                 :icon     i/arrows-up-down
                 :on-click #(>evt [::checklist-subs/toggle-reordering])}
                (if reordering?
                  (tr [:inspection.checklist/reorder-done])
                  (tr [:inspection.checklist/reorder])))
     (cc/button {:kind     :tertiary
                 :size     :small
                 :disabled reordering?
                 :on-click #(>evt [::checklist-subs/set-default-checklist-template
                                   (not (<sub [::checklist-subs/default-checklist-template?]))])}
                (if (<sub [::checklist-subs/default-checklist-template?])
                  (tr [:inspection.checklist/set-default-false])
                  (tr [:inspection.checklist/set-default-true])))]))

(defn result-set-name [tr lang result-set]
  (if (keyword? result-set)
    (tr [result-set])
    (->> result-set
         ((apply juxt inspection-model/checklist-result-set-entity-name-attributes))
         (map #(i18n-model/get-text-from-entities-or-map % lang))
         (str/join "/"))))

(defn checklist-editor-fields [{:keys [tr language]}]
  (let [checklist                    (<sub [::checklist-subs/selected-checklist-template])
        checklist-id                 (:db/id checklist)
        on-save                      #(>evt [::ee/save nil :inspection/edit-checklist-template checklist-id %1 %2])
        selected-result-set          (or (:inspection.checklist/custom-result-set checklist)
                                         (:inspection.checklist/result-set checklist))
        selected-result-set-removed? (du/in-state? :inspection.checklist.result-set.state/removed selected-result-set)
        deficiencies?                (inspection-model/has-deficiencies? (<sub [::gas/group-primary-permit-type]))]
    [:form.grid.grid-cols-2.gap-4.bg-gray-200.px-2.py-4.rounded-sm
     {:on-submit #(.preventDefault %)}
     [:div
      [ui-select/select
       {:id           "inspection-checklist-template-result-set"
        :class        "colspan-1 w-full"
        :light        true
        :label-text   (tr [:inspection.checklist/result-set])
        :allow-empty? false
        :value        selected-result-set
        :val->str     (partial result-set-name tr language)
        :values       (concat inspection-model/checklist-result-sets
                              (<sub [::inspection-checklist-result-sets/checklist-result-sets])
                              (when selected-result-set-removed?
                                [selected-result-set]))
        :on-change    #(apply on-save
                              (if (keyword? %)
                                [:inspection.checklist/result-set %]
                                [:inspection.checklist/custom-result-set (:db/id %)]))}]
      (when selected-result-set-removed?
        [:div.text-ui-red (tr [:inspection.checklist/selected-result-set-removed])])]
     (when deficiencies?
       [:div
        (cc/checkbox
          {:id         "inspection-checklist-template-positive-result-remark"
           :label-text (tr [:inspection.checklist/positive-result-remark?])
           :checked    (-> checklist :inspection.checklist/positive-result-remark? boolean)
           :on-change  #(on-save :inspection.checklist/positive-result-remark? %)})
        (cc/checkbox
          {:id         "inspection-checklist-template-negative-result-deficiency"
           :label-text (tr [:inspection.checklist/negative-result-deficiency?])
           :checked    (-> checklist :inspection.checklist/negative-result-deficiency? boolean)
           :on-change  #(on-save :inspection.checklist/negative-result-deficiency? %)})])]))

(defn checklist-editor
  "Editor for a single inspection checklist template, with its item templates and item groups"
  [{:keys [language tr] :as ctx}]
  (let [checklist         (<sub [::checklist-subs/selected-checklist-template])
        editing-disabled? (<sub [::checklist-subs/reordering?])]
    [:<>
     [ui-sep/horizontal-line]
     [:div.flex.flex-col.mt-8
      [:div.mb-1.flex.justify-between.items-baseline
       [:div.flex.flex-row.items-baseline
        [:div.bold.uppercase.flex
         (checklist-name checklist language)]
        [inspection-checklist-items/i18n-popup-text-editor
         (util/mmap {:title-kw     :inspection.checklist/names
                     :rename-kw    :inspection.checklist/edit
                     :entity       checklist
                     :attr         :inspection.checklist/names
                     :save-command :inspection/edit-checklist-template}
                    tr editing-disabled?)]]
       [checklist-editor-buttons ctx]]
      [:div.mt-6
       [checklist-editor-fields ctx]
       [inspection-checklist-items/editor ctx]]]]))

(defn view-tabbed
  "Inspection checklist template editor for the group admin.
  At the top is the list of all checklist templates for the group.x
  The rest of the view is the editor for the selected template."
  [{:keys [tr] :as ctx}]
  (let [language (<sub [::ui-i18n/editor-language ::gas/allowed-locales])
        ctx      (assoc ctx :language language)]
    [tab-panel/tab-panel
     {:id        "inspection-config-tabs-checklists"
      :class     "mt-8 h-full"
      :tab-class "h-full bg-ui-violet-20"
      :key->str  #(tr [%])
      :tabs      [{:key     :inspection.checklist/configure-checklists
                   :content [:div.mt-6
                             [checklist-selector ctx]
                             (when (<sub [::checklist-subs/selected-checklist-template-id])
                               [checklist-editor ctx])]}
                  {:key     :inspection.checklist/configure-result-sets
                   :content [:div.mt-6
                             [inspection-checklist-result-sets/editor ctx]]}]}]))

(defn copy-checklist-modal
  "Modal for copying checklists from one inspection type to another"
  [{:keys [tr language]}]
  (let [open?                  (<sub [::checklist-subs/copy-modal-open?])
        close-fn               #(>evt [::checklist-subs/close-copy-modal])
        source-kinds           (<sub [::checklist-subs/available-source-template-kinds])
        selected-source-id     (<sub [::checklist-subs/selected-source-template-kind-id])
        source-checklists      (<sub [::checklist-subs/source-checklists])
        selected-checklist-ids (<sub [::checklist-subs/selected-checklist-ids-to-copy])
        target-checklists      (<sub [::checklist-subs/inspection-checklist-templates])
        format-date-time       (<sub [::date-util/date-time-fn])]
    [ui-modal/composed-input-modal {:on-close close-fn
                                    :open     open?}
     [cc/modal-header {:close-modal close-fn
                       :title       (tr [:inspection.checklist/copy-checklists])}]
     [cc/modal-body
      {}
      [:div.space-y-4
       [:div.w-full
        [ui-select/select
         {:id           "source-inspection-type-select"
          :class        "w-full"
          :values       source-kinds
          :val->str     #(inspection-util/template-kind->name % language)
          :value        (when selected-source-id
                          (first (filter (fn [k] (= (:db/id k) selected-source-id)) source-kinds)))
          :on-change    #(>evt [::checklist-subs/select-source-template-kind (:db/id %)])
          :allow-empty? true
          :select-text  (tr [:inspection.checklist/select-source-placeholder])
          :label-text   (tr [:inspection.checklist/select-source-type])}]]

       (when selected-source-id
         [:div
          [:div.flex.justify-between.items-center.mb-2
           [:h4 (tr [:inspection.checklist/select-checklists-to-copy])]
           [:div.flex.gap-2
            [cc/button
             {:kind     :ghost
              :size     :small
              :on-click #(>evt [::checklist-subs/select-all-checklists
                                (mapv :db/id source-checklists)])}
             (tr [:multi-select/select-all])]
            [cc/button
             {:kind     :ghost
              :size     :small
              :on-click #(>evt [::checklist-subs/deselect-all-checklists])}
             (tr [:reporting/deselect-all])]]]

          [:div.border.rounded.p-4.max-h-96.overflow-y-auto
           (if (seq source-checklists)
             (for [checklist source-checklists]
               (let [checked? (contains? (set selected-checklist-ids) (:db/id checklist))]
                 ^{:key (:db/id checklist)}
                 [:label.flex.items-center.p-2.hover:bg-gray-50.cursor-pointer
                  {:for (str "checklist-checkbox-" (:db/id checklist))}
                  [:div.shrink-0
                   [cc/checkbox
                    {:id         (str "checklist-checkbox-" (:db/id checklist))
                     :label-text ""
                     :checked    checked?
                     :on-change  #(>evt [::checklist-subs/toggle-checklist-selection (:db/id checklist)])}]]
                  [:div.flex-1.pl-2
                   [:div.font-medium (checklist-name checklist language)]
                   (when-let [modified-ts (:inspection.checklist/last-modified-ts checklist)]
                     [:div.text-sm.text-gray-500
                      (str (tr [:inspection.checklist/last-modified-label]) ": "
                           (format-date-time modified-ts)
                           (when-let [modified-by (get-in checklist [:inspection.checklist/last-modified-by :user/name])]
                             (str " " (tr [:by]) " " modified-by)))])]]))
             [:div.text-center.text-gray-500.py-4
              (tr [:inspection.checklist/no-checklists-in-type])])]])]]
     [cc/modal-footer
      {}
      [cc/button {:kind     :secondary
                  :on-click close-fn}
       (tr [:cancel])]
      [cc/button {:kind     :primary
                  :disabled (empty? selected-checklist-ids)
                  :on-click #(>evt [::checklist-subs/check-conflicts-and-copy
                                    (filter (fn [c] (contains? (set selected-checklist-ids) (:db/id c))) source-checklists)
                                    target-checklists
                                    language])}
       (tr [:inspection.checklist/copy-n-checklists] [(count selected-checklist-ids)])]]]))

(defn conflict-resolution-modal
  "Modal for resolving checklist name conflicts during copy"
  [{:keys [tr language]}]
  (let [conflicts         (<sub [::checklist-subs/detected-conflicts])
        open?             (boolean (seq conflicts))
        close-fn          #(>evt [::checklist-subs/close-copy-modal])
        selected-strategy (or (<sub [::checklist-subs/selected-conflict-resolution-strategy])
                              :append-suffix)]
    (when open?
      [ui-modal/composed-input-modal {:on-close close-fn
                                      :open     open?}
       [cc/modal-header {:close-modal close-fn
                         :title       (tr [:inspection.checklist/resolve-conflicts])}]
       [cc/modal-body
        {}
        [:div.space-y-4
         [:p (tr [:inspection.checklist/conflicts-detected])]

         [:div.border.rounded.p-4.max-h-48.overflow-y-auto.bg-gray-50
          [:ul.list-disc.pl-4
           (for [conflict conflicts]
             ^{:key (:db/id conflict)}
             [:li.py-1 (checklist-name conflict language)])]]

         [:p.font-medium.mt-4 (tr [:inspection.checklist/how-to-proceed])]

         [:div.mt-4
          [ui-select/select
           {:id           "conflict-resolution-select"
            :class        "w-full"
            :label-text   (tr [:inspection.checklist/conflict-strategy])
            :allow-empty? false
            :value        selected-strategy
            :values       (vec inspection-model/checklist-conflict-resolution-strategies)
            :val->str     #(tr [(keyword "inspection.checklist" (str "conflict-" (name %)))])
            :on-change    #(>evt [::checklist-subs/select-conflict-resolution-strategy %])}]]]]
       [cc/modal-footer
        {}
        [cc/button {:kind     :secondary
                    :on-click close-fn}
         (tr [:cancel])]
        [cc/button {:kind     :primary
                    :on-click #(>evt [::checklist-subs/execute-copy selected-strategy])}
         (tr [:continue])]]])))

(defn export-checklist-modal
  "Modal for exporting checklists from selected inspection type."
  [{:keys [tr language]}]
  (let [open?                  (<sub [::checklist-subs/export-modal-open?])
        close-fn               #(>evt [::checklist-subs/close-export-modal])
        template-kind-id       (<sub [::checklist-subs/selected-template-kind-id])
        source-checklists      (<sub [::checklist-subs/inspection-checklist-templates])
        selected-checklist-ids (<sub [::checklist-subs/selected-checklist-ids-to-export])
        format-date-time       (<sub [::date-util/date-time-fn])]
    [ui-modal/composed-input-modal {:on-close close-fn
                                    :open     open?}
     [cc/modal-header {:close-modal close-fn
                       :title       (tr [:inspection.checklist/export-checklists])}]
     [cc/modal-body
      {}
      [:div.space-y-4
       [:div
        [:div.flex.justify-between.items-center.mb-2
         [:h4 (tr [:inspection.checklist/select-checklists-to-export])]
         [:div.flex.gap-2
          [cc/button
           {:kind     :ghost
            :size     :small
            :on-click #(>evt [::checklist-subs/select-all-export-checklists
                              (mapv :db/id source-checklists)])}
           (tr [:multi-select/select-all])]
          [cc/button
           {:kind     :ghost
            :size     :small
            :on-click #(>evt [::checklist-subs/deselect-all-export-checklists])}
           (tr [:reporting/deselect-all])]]]

        [:div.border.rounded.p-4.max-h-96.overflow-y-auto
         (if (seq source-checklists)
           (for [checklist source-checklists]
             (let [checked? (contains? (set selected-checklist-ids) (:db/id checklist))]
               ^{:key (:db/id checklist)}
               [:label.flex.items-center.hover:bg-gray-50.cursor-pointer
                {:for (str "checklist-checkbox-" (:db/id checklist))}
                [:div.shrink-0
                 [cc/checkbox
                  {:id         (str "checklist-checkbox-" (:db/id checklist))
                   :label-text ""
                   :checked    checked?
                   :on-change  #(>evt [::checklist-subs/toggle-export-checklist-selection (:db/id checklist)])}]]
                [:div.flex-1.pl-2
                 [:div.font-medium (checklist-name checklist language)]
                 (when-let [modified-ts (:inspection.checklist/last-modified-ts checklist)]
                   [:div.text-sm.text-gray-500
                    (str (tr [:inspection.checklist/last-modified-label]) ": "
                         (format-date-time modified-ts)
                         (when-let [modified-by (get-in checklist [:inspection.checklist/last-modified-by :user/name])]
                           (str " " (tr [:by]) " " modified-by)))])]]))
           [:div.text-center.text-gray-500.py-4
            (tr [:inspection.checklist/no-checklists-in-type])])]]]]
     [cc/modal-footer
      {}
      [cc/button {:kind     :secondary
                  :on-click close-fn}
       (tr [:cancel])]
      [cc/button {:kind     :primary
                  :disabled (empty? selected-checklist-ids)
                  :on-click close-fn
                  :href     (str "/api/command/inspection/export-checklist-templates"
                                 "?source-template-kind-id=" template-kind-id
                                 (when (seq selected-checklist-ids)
                                   (str "&" (->> selected-checklist-ids
                                                 (map #(str "checklist-template-ids=" %))
                                                 (str/join "&")))))}
       (tr [:inspection.checklist/export])]]]))

(defn import-conflict-resolution-modal
  "Modal for resolving checklist name conflicts during file import"
  [{:keys [tr]}]
  (let [import-state      (<sub [::checklist-subs/import-conflict-state])
        open?             (boolean import-state)
        conflicts         (:conflicts import-state)
        selected-strategy (or (<sub [::checklist-subs/selected-import-conflict-resolution-strategy])
                              :append-suffix)
        close-fn          (fn []
                            (when-let [{:keys [component-id file-hash]} import-state]
                              (stop-tracking-file component-id file-hash))
                            (>evt [::checklist-subs/close-import-conflict-dialog]))
        language          (<sub [:language])]
    (when open?
      [ui-modal/composed-input-modal {:on-close close-fn
                                      :open     open?}
       [cc/modal-header {:close-modal close-fn
                         :title       (tr [:inspection.checklist/resolve-conflicts])}]
       [cc/modal-body
        {}
        [:div.space-y-4
         [:p (tr [:inspection.checklist/conflicts-detected])]

         [:div.border.rounded.p-4.max-h-48.overflow-y-auto.bg-gray-50
          [:ul.list-disc.pl-4
           (for [{:keys [existing-id existing-names]} conflicts]
             ^{:key existing-id}
             [:li.py-1 (i18n-model/get-text-from-entities-or-map existing-names language)])]]

         [:p.font-medium.mt-4 (tr [:inspection.checklist/how-to-proceed])]

         [:div.mt-4
          [ui-select/select
           {:id           "import-conflict-resolution-select"
            :class        "w-full"
            :label-text   (tr [:inspection.checklist/conflict-strategy])
            :allow-empty? false
            :value        selected-strategy
            :values       (vec inspection-model/checklist-conflict-resolution-strategies)
            :val->str     #(tr [(keyword "inspection.checklist" (str "conflict-" (name %)))])
            :on-change    #(>evt [::checklist-subs/select-import-conflict-resolution-strategy %])}]]]]
       [cc/modal-footer
        {}
        [cc/button {:kind     :secondary
                    :on-click close-fn}
         (tr [:cancel])]
        [cc/button {:kind     :primary
                    :on-click (fn []
                                (let [{:keys [kind-id file component-id file-hash]} import-state]
                                  (execute-import kind-id file component-id file-hash selected-strategy)
                                  (>evt [::checklist-subs/close-import-conflict-dialog])))}
         (tr [:inspection.checklist/import])]]])))

(defn view [ctx]
  [:<>
   [view-tabbed ctx]
   [copy-checklist-modal ctx]
   [conflict-resolution-modal ctx]
   [export-checklist-modal ctx]
   [import-conflict-resolution-modal ctx]])
